import React, { useState, useEffect } from 'react';
import axios from 'axios';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import { GradientSpinner } from '@/components/ui/Spinner';
import Text from '@/components/ui/Text';
import { TCITooltip } from '@/components/ui/Tooltip';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import { Toggle, ToggleOption } from '@/components/ui/Toggle';
import * as Routes from '@/modules/routes';
import Spinner from '@/components/common/Spinner';
import SectionCheckboxList from './SectionCheckboxList';
import Stepper from './Stepper';
import type { ReadingSection } from './types';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';
import useQuestionGeneration from './useQuestionGeneration';
import useFlags from '@/hooks/useFlags';

interface PanelFooterProps {
  canSubmit: boolean;
  isGenerating: boolean;
  mode: string;
  questionCount: number;
  onCancel: () => void;
  onSubmit: () => void;
}

const PanelFooter = ({ canSubmit, isGenerating, mode, questionCount, onCancel, onSubmit }: PanelFooterProps) => {
  if (isGenerating) {
    return (
      <div className="tw-flex tw-gap-2 tw-justify-end tw-px-5 tw-py-3">
        <button
          className={cn(
            'tw-px-2.5 tw-py-2 tw-border tw-border-solid tw-border-red',
            'tw-text-red tw-bg-white tw-rounded tw-text-sm',
            'tw-font-tci tw-cursor-pointer hover:tw-bg-[#fef2f2]',
            'tw-flex tw-items-center tw-gap-1.5',
          )}
          type="button"
          onClick={onCancel}
        >
          <Icon fill={COLORS.red} icon="Stop" size={12} />
          {I18n.t('ai_qb_cancel')}
        </button>
        <button
          disabled
          className={cn(
            'tw-flex tw-items-center tw-justify-center tw-gap-2',
            'tw-px-2.5 tw-py-2 tw-bg-tci-purple tw-text-white',
            'tw-rounded tw-text-sm tw-font-tci',
            'tw-cursor-not-allowed tw-opacity-80 tw-border-none',
          )}
          type="button"
        >
          <GradientSpinner color="white" />
          {mode === 'create' ? I18n.t('ai_qb_creating_questions') : I18n.t('modifying_existing_questions')}
        </button>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-justify-end tw-px-5 tw-py-3">
      <TCITooltip disabled={canSubmit} side="left">
        <TCITooltip.Trigger asChild>
          <span className="tw-inline-block">
            <button
              aria-disabled={!canSubmit}
              className={cn(
                'tw-flex tw-items-center tw-justify-center tw-gap-2',
                'tw-px-5 tw-py-2 tw-rounded tw-text-sm',
                'tw-font-tci tw-border-none',
                canSubmit
                  ? 'tw-bg-tci-purple tw-text-white tw-cursor-pointer hover:tw-bg-[#3F1587]'
                  : 'tw-bg-dark-grey tw-text-white tw-cursor-not-allowed',
              )}
              type="button"
              onClick={canSubmit ? onSubmit : undefined}
            >
              <Icon fill="white" icon="Sparkle" size={12} />
              {mode === 'create'
                ? I18n.t('ai_qb_create_questions', { count: questionCount }) : I18n.t('modify_existing_questions', { count: questionCount })}
            </button>
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {I18n.t('ai_qb_fill_fields_tooltip')}
        </TCITooltip.Content>
      </TCITooltip>
    </div>
  );
};

interface AIQuestionBuilderPanelProps {
  sections: ReadingSection[];
}

const AIQuestionBuilderPanel = ({ sections }: AIQuestionBuilderPanelProps) => {
  const {
    answerCount,
    assessmentID,
    error,
    questionCount,
    questions,
    selectedSectionIDs,
    creationMode,
    closePanel,
    deselectAllSections,
    selectAllSections,
    setAnswerCount,
    setQuestionCount,
    setQuestions,
    toggleSectionSelected,
    setCreationMode,
  } = useAIQuestionBuilderStore();

  const generation = useQuestionGeneration();
  const sectionIDs = sections.map(s => s.id);

  const [isLoading, setIsLoading] = useState(true);

  const { showAiModify } = useFlags();

  // Fetch fresh question data when opening modal
  useEffect(() => {
    if (assessmentID) {
      axios.get(Routes.plato_api_questions_summary_assessment_path(assessmentID))
        .then((response) => {
          setQuestions(response.data.questions);
          setIsLoading(false);
        })
        .catch((e) => {
          console.error('Failed to fetch questions summary:', e);
          setIsLoading(false);
        });
    }

    return () => {
      setIsLoading(true);
    };
  }, [assessmentID, setQuestions]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel]);

  const modeOptions: ToggleOption[] = [
    { label: I18n.t('create_new'), value: 'create' },
    { label: I18n.t('modify_existing'), value: 'modify' },
  ];

  const totalQuestionsCount = questions?.length ?? 0;
  const questionsToModifyCount = questions
    ?.filter(q => answerCount !== null && q.number_of_answers !== answerCount).length ?? 0;

  if (isLoading) return <Spinner />;

  return (
    <div
      className={cn(
        'tw-fixed tw-top-0 tw-right-0 tw-w-[38.75rem] tw-h-full tw-bg-white tw-z-[1050]',
        'tw-flex tw-flex-col tw-border-l-2 tw-border-grey',
        'tw-shadow-lg',
      )}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
        <Text variant="heading2" weight="bold">
          {I18n.t('ai_qb_panel_title')}
        </Text>
        <button
          aria-label="Close panel"
          className={cn(
            'tw-text-dark-grey tw-bg-transparent tw-border-none',
            'tw-cursor-pointer tw-w-6 tw-h-6 tw-flex tw-items-center',
            'tw-justify-center hover:tw-text-tci-black',
          )}
          type="button"
          onClick={closePanel}
        >
          <Icon icon="Close" size={16} />
        </button>
      </div>

      <hr className="tw-border-t tw-border-grey tw-m-0" />

      {questions.length > 0 && showAiModify && (
        <Toggle
          firstButtonClassName="tw-rounded-l tw-font-tci"
          options={modeOptions}
          secondButtonClassName="tw-rounded-r tw-font-tci"
          value={creationMode}
          wrapperClassName="tw-flex tw-justify-center tw-h-12 tw-py-4 tw-px-20"
          onChange={(value) => {
            setCreationMode(value as 'create' | 'modify');
            deselectAllSections();
          }}
        />
      )}

      <div className="tw-overflow-y-auto tw-px-5 tw-pt-4">
        {error && (
          <div
            className={cn(
              'tw-mb-3 tw-px-3 tw-py-2.5 tw-bg-[#d2232a0d]',
              'tw-border tw-border-solid tw-border-[#d2232a] tw-rounded-[10px]',
              'tw-flex tw-items-start tw-gap-3',
            )}
          >
            <i
              aria-hidden="true"
              className="fa fa-exclamation-triangle tw-text-[#d2232a] tw-text-2xl tw-mt-0.5"
            />
            <div>
              <Text className="tw-text-[#d2232a]" weight="bold">
                {I18n.t('ai_qb_error_title')}
              </Text>
              <Text className="tw-text-dark-grey" size="sm">
                {error}
              </Text>
            </div>
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-1 tw-mb-4">
          <Text className="!tw-text-[#777770] tw-pb-2" tag="span">
            {creationMode === 'create' ? I18n.t('ai_qb_mode_create_description') : I18n.t('ai_qb_mode_modify_description')}
          </Text>
          <Text tag="span" variant="label" weight="semibold">
            {I18n.t('ai_qb_question_type')}
          </Text>
          <div
            className={cn(
              'tw-bg-light-grey tw-border tw-border-solid',
              'tw-border-grey tw-rounded-md',
              'tw-h-9 tw-flex tw-items-center tw-px-4',
            )}
          >
            <Text size="sm" tag="span" variant="body">
              {I18n.t('ai_qb_multiple_choice')}
            </Text>
          </div>
        </div>

        {creationMode === 'create' && (
          <SectionCheckboxList
            disabled={generation.isGenerating}
            sections={sections}
            selectedIDs={selectedSectionIDs}
            onDeselectAll={deselectAllSections}
            onSelectAll={() => selectAllSections(sectionIDs)}
            onToggle={toggleSectionSelected}
          />
        )}

        <Stepper
          disabled={generation.isGenerating}
          label={I18n.t('ai_qb_num_answer_choices')}
          max={6}
          min={2}
          value={answerCount}
          onChange={setAnswerCount}
        />

        {creationMode === 'modify' && (
          <div
            className={cn(
              'tw-mb-3 tw-px-3 tw-py-2.5 tw-rounded-[10px]',
              'tw-border tw-border-solid',
              'tw-flex tw-items-center tw-gap-3',
              questionsToModifyCount !== 0
                ? 'tw-bg-[#faf5e8] tw-border-[#E19617]'
                : 'tw-bg-[#e8f5ea] tw-border-green',
            )}
          >
            <Icon
              fill={questionsToModifyCount === 0 ? COLORS['green'] : COLORS['dark-grey']}
              icon={questionsToModifyCount === 0 ? 'CheckmarkCircle' : 'InfoAlternate'}
              size={30}
            />
            <div className="tw-flex tw-flex-col tw-gap-2 tw-p-2">
              <Text className="tw-text-dark-grey tw-m-auto" size="sm" variant="body" weight="bold">
                {questionsToModifyCount !== 0
                  ? I18n.t('ai_qb_modify_summary', {
                    answer_count: answerCount,
                    modify_count: questionsToModifyCount,
                    total_count: totalQuestionsCount,
                  })
                  : I18n.t('ai_qb_no_questions_to_modify', {
                    answer_count: answerCount,
                    total_count: totalQuestionsCount,
                  })
                }
              </Text>
              <Text className="!tw-text-gray-700" size="xs" variant="body">
                {questionsToModifyCount !== 0
                  ? I18n.t('ai_qb_modify_message', { answer_count: answerCount })
                  : I18n.t('ai_qb_no_questions_to_modify_message')}
              </Text>
            </div>
          </div>
        )}

        {creationMode === 'create' && (
          <Stepper
            disabled={generation.isGenerating}
            label={I18n.t('ai_qb_num_questions')}
            max={20}
            min={1}
            value={questionCount}
            onChange={setQuestionCount}
          />
        )}
      </div>

      <hr className="tw-border-t tw-border-grey tw-m-0" />

      <PanelFooter
        canSubmit={generation.canSubmit}
        isGenerating={generation.isGenerating}
        mode={creationMode}
        questionCount={creationMode === 'modify' ? questionsToModifyCount : (questionCount ?? 0)}
        onCancel={generation.handleCancel}
        onSubmit={generation.handleSubmit}
      />
    </div>
  );
};

export default AIQuestionBuilderPanel;
